import createClient from 'openapi-fetch';
import type {
  ApiPaths,
  DeviationEventPage,
  DeviationRun,
  DeviationRunCreate,
  EdgePatch,
  EngineEvaluation,
  EvaluationRequest,
  ExplorerMove,
  GamePage,
  Game,
  GameRepertoireMatch,
  Job,
  LineCreate,
  LineCreateResult,
  LineDeleteResult,
  LinePage,
  MoveAdd,
  PgnImport,
  Problem,
  ProviderAccount,
  ProviderAccountCreate,
  ProviderSyncCreate,
  Repertoire,
  RepertoireCreate,
  RepertoireEdge,
  RepertoireLine,
  RepertoireNode,
  RepertoirePage,
  ReviewGradeCreate,
  ReviewItemState,
  ReviewQueue,
  ReviewQueueMode,
  ReviewSummary,
  ZobristHash,
} from './types';
import { ApiError, toProblem } from './errors';
import { clampLineName } from '../lib/line-name';

/** Lines per request (the API's maximum `limit`). */
const LINE_PAGE_SIZE = 200;
/** Stop after this many pages (40,000 rows) rather than loop on a misbehaving cursor. */
const MAX_LINE_PAGES = 200;

/** Shared configuration for the generated OpenAPI client. */
export interface ApiConfig {
  baseUrl: string;
  workspaceId: string;
  /** Returns a fresh short-lived OIDC access token, or null when unauthenticated. */
  getAccessToken?: () => string | null;
  /** Invoked when the server answers 401 so the app can drop the stale token. */
  onUnauthorized?: () => void;
  /** Attempt a silent refresh; returns a new token to retry with, or null. */
  onRefresh?: () => Promise<string | null>;
  /** Injectable fetch (used by the §4 mock server and tests). */
  fetch?: typeof fetch;
}

type OpenApiClient = ReturnType<typeof createClient<ApiPaths>>;

/** A typed facade over the generated client with workspace/auth headers injected. */
export class ChessRepsApi {
  private readonly client: OpenApiClient;
  private readonly workspaceId: string;
  private readonly baseUrl: string;
  private readonly onUnauthorized?: () => void;
  private readonly getAccessToken?: () => string | null;

  constructor(config: ApiConfig) {
    this.workspaceId = config.workspaceId;
    this.baseUrl = config.baseUrl;
    this.onUnauthorized = config.onUnauthorized;
    this.getAccessToken = config.getAccessToken;
    const baseFetch = config.fetch ?? globalThis.fetch;
    const getToken = config.getAccessToken;
    const onRefresh = config.onRefresh;

    const fetchWithAuth: typeof fetch = async (input, init) => {
      const doFetch = (request: RequestInfo | URL, token: string | null) => {
        if (token === null) return baseFetch(request, init);
        // openapi-fetch passes a Request object; copy it with the bearer header so
        // method/body/headers are preserved. A retry's fresh token replaces the
        // expired one.
        if (request instanceof Request) {
          const headers = new Headers(request.headers);
          headers.set('Authorization', `Bearer ${token}`);
          return baseFetch(new Request(request, { headers }), init);
        }
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${token}`);
        return baseFetch(request, { ...init, headers });
      };

      // Building a Request from a Request consumes its body, so keep an untouched
      // copy for retrying a POST/PATCH/PUT after a refresh.
      const retryInput = input instanceof Request && onRefresh ? input.clone() : input;
      const token = getToken?.() ?? null;
      const response = await doFetch(input, token);
      // On a 401 (expired access token), try one silent refresh and retry.
      if (response.status === 401 && onRefresh) {
        const refreshed = await onRefresh();
        if (refreshed) return doFetch(retryInput, refreshed);
      }
      return response;
    };

    this.client = createClient<ApiPaths>({
      baseUrl: config.baseUrl,
      fetch: fetchWithAuth,
    });
  }

  private ws(): { 'X-Workspace-ID': string } {
    return { 'X-Workspace-ID': this.workspaceId };
  }

  async getProfile(): Promise<{ username: string | null }> {
    return this.unwrap(await this.client.GET('/account/profile'));
  }

  async updateProfile(username: string): Promise<{ username: string | null }> {
    return this.unwrap(await this.client.PUT('/account/profile', { body: { username } }));
  }

  async listRepertoires(limit = 50): Promise<RepertoirePage> {
    const result = await this.client.GET('/repertoires', {
      params: { query: { limit }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  async createRepertoire(
    body: RepertoireCreate,
    idempotencyKey: string,
  ): Promise<{ data: Repertoire; etag: string | null }> {
    const result = await this.client.POST('/repertoires', {
      params: { header: { ...this.ws(), 'Idempotency-Key': idempotencyKey } },
      body,
    });
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  async getRepertoire(repertoireId: string): Promise<{ data: Repertoire; etag: string | null }> {
    const result = await this.client.GET('/repertoires/{repertoireId}', {
      params: { path: { repertoireId }, header: this.ws() },
    });
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  /** Schedule a repertoire for deletion (§4); requires the current version. */
  async deleteRepertoire(repertoireId: string, ifMatch: string): Promise<void> {
    const result = await this.client.DELETE('/repertoires/{repertoireId}', {
      params: {
        path: { repertoireId },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
    });
    if (result.response.status !== 204) {
      throw new ApiError(toProblem(result.error, result.response.status), result.response.status);
    }
  }

  /** Delete a line, optionally pruning nodes it made unreachable (§4). */
  async deleteLine(
    repertoireId: string,
    lineId: string,
    ifMatch: string,
    pruneUnreachable = true,
  ): Promise<LineDeleteResult> {
    const result = await this.client.DELETE('/repertoires/{repertoireId}/lines/{lineId}', {
      params: {
        path: { repertoireId, lineId },
        query: { pruneUnreachable },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
    });
    return this.unwrap(result);
  }

  /**
   * Active lines only. The server soft-deletes (`active: false`, kept for recovery)
   * and still lists those rows, including the one a builder save replaces.
   */
  async listLines(repertoireId: string, containsHash?: ZobristHash): Promise<LinePage> {
    // Walk every page: the server pages newest-first and includes soft-deleted rows,
    // so reading one page and filtering afterwards hid older active lines once enough
    // saves had accumulated.
    const items: LinePage['items'] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_LINE_PAGES; page += 1) {
      const result = await this.client.GET('/repertoires/{repertoireId}/lines', {
        params: {
          path: { repertoireId },
          query: {
            limit: LINE_PAGE_SIZE,
            ...(cursor ? { cursor } : {}),
            ...(containsHash ? { containsHash } : {}),
          },
          header: this.ws(),
        },
      });
      const body = this.unwrap(result);
      items.push(...body.items.filter((line) => line.active));
      if (!body.nextCursor || body.nextCursor === cursor) break;
      cursor = body.nextCursor;
    }
    return { items, nextCursor: null };
  }

  async getLine(repertoireId: string, lineId: string): Promise<RepertoireLine> {
    const result = await this.client.GET('/repertoires/{repertoireId}/lines/{lineId}', {
      params: { path: { repertoireId, lineId }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  async getNode(
    repertoireId: string,
    zobristHash: ZobristHash,
    positionId: string,
  ): Promise<{ data: RepertoireNode; etag: string | null }> {
    const result = await this.client.GET(
      '/repertoires/{repertoireId}/nodes/{zobristHash}/{positionId}',
      {
        params: {
          path: { repertoireId, zobristHash, positionId },
          header: this.ws(),
        },
      },
    );
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  async updateEdge(
    repertoireId: string,
    edgeId: string,
    ifMatch: string,
    patch: EdgePatch,
  ): Promise<{ data: RepertoireEdge; etag: string | null }> {
    const result = await this.client.PATCH('/repertoires/{repertoireId}/edges/{edgeId}', {
      params: {
        path: { repertoireId, edgeId },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
      body: patch,
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  async renameRepertoire(repertoireId: string, name: string, ifMatch: string): Promise<Repertoire> {
    const result = await this.client.PATCH('/repertoires/{repertoireId}', {
      params: {
        path: { repertoireId },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: { name },
    });
    return this.unwrap(result);
  }

  async renameLine(
    repertoireId: string, lineId: string, name: string, ifMatch: string,
  ): Promise<RepertoireLine> {
    const result = await this.client.PATCH('/repertoires/{repertoireId}/lines/{lineId}', {
      params: {
        path: { repertoireId, lineId },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: { name },
    });
    return this.unwrap(result);
  }

  /** Disable a repertoire branch at this edge; shared downstream nodes stay intact. */
  async disableEdge(repertoireId: string, edgeId: string, ifMatch: string): Promise<void> {
    const result = await this.client.DELETE('/repertoires/{repertoireId}/edges/{edgeId}', {
      params: {
        path: { repertoireId, edgeId },
        header: { ...this.ws(), 'If-Match': ifMatch },
      },
    });
    if (result.response.status !== 204) {
      throw new ApiError(toProblem(result.error, result.response.status), result.response.status);
    }
  }

  async createLine(
    repertoireId: string,
    idempotencyKey: string,
    ifMatch: string,
    body: LineCreate,
  ): Promise<{ data: LineCreateResult; etag: string | null }> {
    const result = await this.client.POST('/repertoires/{repertoireId}/lines', {
      params: {
        path: { repertoireId },
        header: {
          ...this.ws(),
          'Idempotency-Key': idempotencyKey,
          'If-Match': ifMatch,
        },
      },
      body: body.name == null ? body : { ...body, name: clampLineName(body.name) },
    });
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  async explorePosition(fen: string, side?: 'white' | 'black'): Promise<ExplorerMove[]> {
    const result = await this.client.GET('/games/explorer', {
      params: { query: { fen, side }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  async addMove(
    repertoireId: string,
    ifMatch: string,
    idempotencyKey: string,
    body: MoveAdd,
  ): Promise<{ data: RepertoireEdge; etag: string | null }> {
    const result = await this.client.POST('/repertoires/{repertoireId}/moves', {
      params: {
        path: { repertoireId },
        header: {
          ...this.ws(),
          'If-Match': ifMatch,
          'Idempotency-Key': idempotencyKey,
        },
      },
      body,
    });
    return {
      data: this.unwrap(result),
      etag: result.response.headers.get('ETag'),
    };
  }

  async getJob(jobId: string): Promise<Job> {
    const result = await this.client.GET('/jobs/{jobId}', {
      params: { path: { jobId }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  /** Submit a PGN import; returns the durable import job (§4, §6.4). */
  async createImport(body: PgnImport, idempotencyKey: string): Promise<Job> {
    const result = await this.client.POST('/imports', {
      params: { header: { ...this.ws(), 'Idempotency-Key': idempotencyKey } },
      body,
    });
    return this.unwrap(result);
  }

  /**
   * Request an engine evaluation. A cache hit returns the evaluation inline
   * (200); a miss returns the durable job (202) to track (§4, §7.5).
   */
  async requestEvaluation(
    body: EvaluationRequest,
    idempotencyKey: string,
  ): Promise<{ kind: 'evaluation'; evaluation: EngineEvaluation } | { kind: 'job'; job: Job }> {
    const result = await this.client.POST('/evaluations', {
      params: { header: { ...this.ws(), 'Idempotency-Key': idempotencyKey } },
      body,
    });
    // The two success bodies differ; discriminate on status rather than shape.
    if (result.response.status === 202) {
      return { kind: 'job', job: this.unwrap(result) as Job };
    }
    return {
      kind: 'evaluation',
      evaluation: this.unwrap(result) as EngineEvaluation,
    };
  }

  async getEvaluation(
    zobristHash: ZobristHash,
    positionId: string,
    evaluationId: string,
  ): Promise<EngineEvaluation> {
    const result = await this.client.GET(
      '/positions/{zobristHash}/{positionId}/evaluations/{evaluationId}',
      {
        params: {
          path: { zobristHash, positionId, evaluationId },
          header: this.ws(),
        },
      },
    );
    return this.unwrap(result);
  }

  async listGames(limit = 50, cursor?: string): Promise<GamePage> {
    const result = await this.client.GET('/games', {
      params: { query: { limit, cursor }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  /** Rank the caller's repertoires for each game by how far it followed them (§4, v1.8). */
  async matchGameRepertoires(gameIds: string[]): Promise<GameRepertoireMatch[]> {
    const result = await this.client.GET('/games/repertoire-matches', {
      params: { query: { gameIds }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  async getGame(gameId: string): Promise<Game> {
    const result = await this.client.GET('/games/{gameId}', {
      params: { path: { gameId }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  /** Start deviation detection for a game; returns the durable job (§4, §6.4). */
  async createDeviationRun(
    gameId: string,
    body: DeviationRunCreate,
    idempotencyKey: string,
  ): Promise<Job> {
    const result = await this.client.POST('/games/{gameId}/deviation-runs', {
      params: {
        path: { gameId },
        header: { ...this.ws(), 'Idempotency-Key': idempotencyKey },
      },
      body,
    });
    return this.unwrap(result);
  }

  async getDeviationRun(runId: string): Promise<DeviationRun> {
    const result = await this.client.GET('/deviation-runs/{runId}', {
      params: { path: { runId }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  async listDeviationEvents(runId: string, limit = 50, cursor?: string): Promise<DeviationEventPage> {
    const result = await this.client.GET('/deviation-runs/{runId}/events', {
      params: { path: { runId }, query: { limit, cursor }, header: this.ws() },
    });
    return this.unwrap(result);
  }

  // ------------------------------------------------------ spaced repetition

  /** Due/new counts and a 7-day forecast, optionally below one position (§4, v1.7). */
  async getReviewSummary(repertoireId: string, scope?: ReviewScope): Promise<ReviewSummary> {
    const result = await this.client.GET('/repertoires/{repertoireId}/review/summary', {
      params: { path: { repertoireId }, query: scopeQuery(scope), header: this.ws() },
    });
    return this.unwrap(result);
  }

  /** Review items in line order, each with the moves that lead to it (§4, v1.7). */
  async getReviewQueue(
    repertoireId: string,
    mode: ReviewQueueMode,
    options: { limit?: number; scope?: ReviewScope } = {},
  ): Promise<ReviewQueue> {
    const result = await this.client.GET('/repertoires/{repertoireId}/review/queue', {
      params: {
        path: { repertoireId },
        query: { mode, limit: options.limit, ...scopeQuery(options.scope) },
        header: this.ws(),
      },
    });
    return this.unwrap(result);
  }

  /** Record one graded answer. Reviews never change the repertoire version. */
  async gradeReview(
    repertoireId: string,
    body: ReviewGradeCreate,
    idempotencyKey: string,
  ): Promise<ReviewItemState> {
    const result = await this.client.POST('/repertoires/{repertoireId}/review', {
      params: {
        path: { repertoireId },
        header: { ...this.ws(), 'Idempotency-Key': idempotencyKey },
      },
      body,
    });
    return this.unwrap(result);
  }

  // ------------------------------------------------------- provider game sync

  /** List the workspace's linked chess.com / Lichess accounts (§4, v1.3). */
  async listProviderAccounts(): Promise<ProviderAccount[]> {
    const result = await this.client.GET('/provider-accounts', {
      params: { header: this.ws() },
    });
    return this.unwrap(result);
  }

  /**
   * Link a chess-site username. Linking is idempotent server-side: re-linking the
   * same username returns the existing link with its sync watermark intact.
   */
  async linkProviderAccount(body: ProviderAccountCreate): Promise<ProviderAccount> {
    const result = await this.client.POST('/provider-accounts', {
      params: { header: this.ws() },
      body,
    });
    return this.unwrap(result);
  }

  /** Remove a link. Games already imported through it are kept. */
  async unlinkProviderAccount(providerAccountId: string): Promise<void> {
    const result = await this.client.DELETE('/provider-accounts/{providerAccountId}', {
      params: { path: { providerAccountId }, header: this.ws() },
    });
    if (result.response.status !== 204) {
      throw new ApiError(toProblem(result.error, result.response.status), result.response.status);
    }
  }

  /** Pull this account's games since the last sync; returns the durable job (§6.4). */
  async syncProviderAccount(
    providerAccountId: string,
    body: ProviderSyncCreate,
    idempotencyKey: string,
  ): Promise<Job> {
    const result = await this.client.POST('/provider-accounts/{providerAccountId}/syncs', {
      params: {
        path: { providerAccountId },
        header: { ...this.ws(), 'Idempotency-Key': idempotencyKey },
      },
      body,
    });
    return this.unwrap(result);
  }

  /** Absolute/relative URL for the durable job-event SSE stream (§4.4.7). */
  jobEventsUrl(jobId: string): string {
    const base = this.baseUrl.replace(/\/$/, '');
    return `${base}/jobs/${jobId}/events`;
  }

  /**
   * Auth + workspace headers for that stream. It is read with a raw fetch rather
   * than openapi-fetch, so the bearer and `X-Workspace-ID` must be passed explicitly.
   */
  jobEventsHeaders(): Record<string, string> {
    const token = this.getAccessToken?.() ?? null;
    return token === null ? this.ws() : { ...this.ws(), Authorization: `Bearer ${token}` };
  }

  /** Unwrap an openapi-fetch result, mapping RFC 9457 problems to ApiError. */
  private unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
    if (result.data !== undefined) return result.data;
    const status = result.response.status;
    if (status === 401) this.onUnauthorized?.();
    throw new ApiError(toProblem(result.error, status), status);
  }
}

/** Limit a review to the region below one repertoire position. */
export interface ReviewScope {
  zobristHash: ZobristHash;
  positionId: string;
}

function scopeQuery(scope?: ReviewScope): { fromHash?: string; fromPositionId?: string } {
  return scope ? { fromHash: scope.zobristHash, fromPositionId: scope.positionId } : {};
}

/** RFC 9457 `code` values the UI branches on (specification §4.3). */
export const ERROR_CODES = {
  VERSION_MISMATCH: 'VERSION_MISMATCH',
  DAG_CYCLE: 'DAG_CYCLE',
  MAIN_MOVE_EXISTS: 'MAIN_MOVE_EXISTS',
  PRECONDITION_REQUIRED: 'PRECONDITION_REQUIRED',
  ILLEGAL_MOVE: 'ILLEGAL_MOVE',
  SRS_ITEM_NOT_SCHEDULED: 'SRS_ITEM_NOT_SCHEDULED',
} as const;

export function isApiError(error: unknown, code?: string): error is ApiError {
  return (
    error instanceof ApiError && (code === undefined || (error.problem as Problem).code === code)
  );
}
